import React, { Component } from "react";
import { toast } from "react-toastify";
import { getCrossSellReport } from "../../Endpoint/Admin/index";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import { C, Panel, Btn, Table, Chip, StatRow, MiniStat } from "../../components/Console/ConsoleUI";
import PeriodFilter, { allTime, periodToParams, periodLabel } from "../../components/Common/PeriodFilter";
import { KIND_LABEL } from "./crossSellMeta";

/**
 * Cross-sell report — how each rule and each built-in suggestion performed:
 * seen → tapped → added → bought, and the revenue those purchases brought.
 *
 * Rows come straight from GET /api/crosssell/report (branch-scoped there).
 * Built-in suggestions have no rule; the server names them "Built-in: …".
 * Rates are null when there is nothing to divide by, and show as "—".
 */

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const count = (n) => Number(n || 0).toLocaleString();
const pct = (n) => (n == null ? "—" : `${n}%`);
const rate = (a, b) => (b ? Math.round((a / b) * 1000) / 10 : null);

class CrossSellReport extends Component {
  constructor(props) {
    super(props);
    this.state = { isLoaded: false, rows: [], period: allTime() };
  }

  componentDidMount() {
    this.loadReport();
  }

  loadReport = () => {
    this.setState({ isLoaded: false });
    getCrossSellReport(periodToParams(this.state.period)).then((res) => {
      if (res && res.status === 200) {
        // A rule, or a built-in source, is one row — together they are unique.
        const rows = (res.data || []).map((r) => ({ ...r, key: `${r.ruleId || "builtin"}:${r.source || ""}` }));
        this.setState({ isLoaded: true, rows });
      } else {
        this.setState({ isLoaded: true, rows: [] });
        toast.error((res && res.message) || "Could not load the cross-sell report");
      }
    });
  };

  handlePeriodChange = (period) => this.setState({ period }, this.loadReport);

  columns() {
    const num = (key) => (r) => <span style={{ color: C.ink }}>{count(r[key])}</span>;
    return [
      {
        key: "name",
        label: "Offer",
        render: (r) => (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 500, color: C.ink }}>{r.name}</span>
            {!r.ruleId && <Chip tone="neutral">Built-in</Chip>}
            {r.deleted && <Chip tone="bad">Deleted</Chip>}
          </div>
        ),
      },
      {
        key: "kind",
        label: "Kind",
        render: (r) => <span style={{ fontSize: 13, color: C.slate }}>{r.kind && KIND_LABEL[r.kind] ? KIND_LABEL[r.kind].label : "—"}</span>,
      },
      { key: "impressions", label: "Seen", render: num("impressions") },
      { key: "clicks", label: "Tapped", render: num("clicks") },
      { key: "clickRate", label: "Tap rate", render: (r) => <span style={{ color: C.slate }}>{pct(r.clickRate)}</span> },
      { key: "added", label: "Added", render: num("added") },
      { key: "conversions", label: "Bought", render: num("conversions") },
      { key: "conversionRate", label: "Buy rate", render: (r) => <span style={{ color: C.slate }}>{pct(r.conversionRate)}</span> },
      { key: "revenue", label: "Revenue", render: (r) => <span style={{ color: C.ink, fontWeight: 500 }}>{money(r.revenue)}</span> },
    ];
  }

  render() {
    const { isLoaded, rows, period } = this.state;
    const sum = (k) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
    const impressions = sum("impressions");
    const clicks = sum("clicks");
    const conversions = sum("conversions");

    return (
      <ConsolePage
        title="Cross-sell report"
        subtitle={`${periodLabel(period)}${isLoaded ? "" : " · loading…"}`}
        actions={
          <Btn kind="quiet" icon="arrow_back" onClick={() => this.props.history.push("/crosssell")}>
            Back to rules
          </Btn>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <PeriodFilter value={period} onChange={this.handlePeriodChange} />

          <StatRow columns={5}>
            <MiniStat label="Seen" value={count(impressions)} hint="offer impressions" />
            <MiniStat label="Tapped" value={count(clicks)} hint={`${pct(rate(clicks, impressions))} of views`} />
            <MiniStat label="Added to cart" value={count(sum("added"))} />
            <MiniStat label="Bought" value={count(conversions)} hint={`${pct(rate(conversions, clicks))} of taps`} />
            <MiniStat label="Revenue" value={money(sum("revenue"))} hint="from bought offers" />
          </StatRow>

          <Panel
            title="By offer"
            subtitle="A purchase counts when the guest buys an offered item within 24 hours of tapping or adding it."
          >
            <div style={{ marginTop: 14 }}>
              {isLoaded ? (
                <Table
                  columns={this.columns()}
                  rows={rows}
                  rowKey="key"
                  empty="No offers were shown in this period"
                />
              ) : (
                <div style={{ padding: "50px 0", textAlign: "center", color: C.taupeLight }}>Loading report…</div>
              )}
            </div>
          </Panel>
        </div>
      </ConsolePage>
    );
  }
}

export default CrossSellReport;
